import { Component, Inject, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MediaObserver, MediaChange } from '@angular/flex-layout';

import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import { MatStepper } from '@angular/material/stepper';

import { DeviceDetectorService } from 'ngx-device-detector';

import { PdfDocumentViewerDialogComponent } from '../pdf-document-viewer-dialog/pdf-document-viewer-dialog.component';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';
import { UserService } from '../../services/user.service';
import { ContractService } from '../../services/contract.service';
import { UtilityService } from '../../services/utility.service';

import { IpAddress } from '../../models/user';
import { EsignNoteDetail, EsignLegalTextListItem, EsignData, EsignContractRequest } from '../../models/contract';
import { PdfDocumentViewerDialogData } from '../../models/pdf-document-viewer';

@Component({
  selector: 'app-esign-note',
  templateUrl: './esign-note.component.html',
  styleUrls: ['./esign-note.component.scss']
})
export class EsignNoteComponent implements OnInit {
  @ViewChild('esignNoteStepper', { static: false }) esignNoteStepper: MatStepper;

  private _handleError: HandleError;
  private _logging = environment.logging;
  private _display = environment.display;
  private _apiSettings = environment.restAPI;
  private _basePDFUrl = '';

  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  userFullName = '';
  participantIdString = '';
  participantId: number = null;
  noteIdString = '';
  noteId: number = null;
  relationshipType: string = null;

  publicIpAddress: IpAddress = {
    ip: null
  };

  esignNoteDetail: EsignNoteDetail;
  loanCode: string = null;

  esignLegalTextList: EsignLegalTextListItem[] = [];
  esignLegalTextFound = false;

  showSpinner = false;
  showEsignSpinner = false;
  showEsignSuccess = false;
  showEsignFailure = false;
  esignProcessed = false;

  step1Complete = false;
  step2Complete = false;
  step1Editable = true;
  step2Editable = true;

  constructor(
    @Inject('BASE_URL') baseUrl: string,
    private _route: ActivatedRoute,
    private _router: Router,
    private _errorHandler: ErrorHandler,
    private _userService: UserService,
    private _contractService: ContractService,
    private _utilityService: UtilityService,
    private _deviceService: DeviceDetectorService,
    public pdfDocumentViewerDialog: MatDialog,
    public media: MediaObserver
  ) {
    this._handleError = _errorHandler.createHandleError('EsignNoteComponent');
    this._basePDFUrl = baseUrl;
    this.userFullName = this._userService.getFullName();
    this.participantId = this._userService.getParticipantId();
    this.participantIdString = this.participantId.toString();
    this.noteId = this._userService.getNoteId();
    this.noteIdString = this.noteId.toString();
    this.relationshipType = this._userService.getRelationshipType();
    this._userService.clearNoteId();
    this._userService.clearRelationshipType();
    this.getData();
    this.scrollToTop();
  }

  ngOnInit() {}

  getData(): void {
    this.showSpinner = true;
    this.getPublicIpAddress();
    this.getEsignNoteDetail(this.noteId);
  }

  getPublicIpAddress(): void {
    this._userService.getPublicIpAddress().subscribe(
      result => {
        this.publicIpAddress = result;
        if (this._logging.serviceCallTraceLog) {
          console.log('EsignNoteComponent.getPublicIpAddress :: result.ip = ' + result.ip);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('EsignNoteComponent.getPublicIpAddress :: result = ' + JSON.stringify(result));
        }
      },
      error => this._handleError('getPublicIpAddress', error)
    );
  }

  getEsignNoteDetail(noteId: number): void {
    this.showSpinner = true;
    this._contractService.getEsignNoteDetail(noteId).subscribe(
      result => {
        this.esignNoteDetail = result;
        if (this._logging.serviceCallTraceLog) {
          console.log('EsignNoteComponent.getEsignNoteDetail :: result.noteId = ' + result.noteId);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('EsignNoteComponent.getEsignNoteDetail :: result = ' + JSON.stringify(result));
        }

        if (
          this.esignNoteDetail !== undefined &&
          this.esignNoteDetail !== null &&
          this.esignNoteDetail.loanCode !== undefined &&
          this.esignNoteDetail.loanCode !== null
        ) {
          this.loanCode = this.esignNoteDetail.loanCode;
        } else {
          this.loanCode = null;
        }

        this.getEsignLegalTextList(noteId);
      },
      error => this._handleError('getEsignNoteDetail', error)
    );
  }

  getEsignLegalTextList(noteId: number): void {
    this.showSpinner = true;
    this._contractService.getEsignLegalTextList(noteId).subscribe(
      resultArray => {
        this.esignLegalTextList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('EsignNoteComponent.getEsignLegalTextList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('EsignNoteComponent.getEsignLegalTextList :: result = ' + JSON.stringify(resultArray));
        }

        this.esignLegalTextFound = resultArray.length > 0;

        this.getDataComplete();
      },
      error => this._handleError('getEsignLegalTextList', error)
    );
  }

  getDataComplete(): void {
    this.showSpinner = false;
  }

  viewDisclosures(): void {
    if (this._userService.isLoggedIn()) {
      const pdfDocumentViewerDialogData: PdfDocumentViewerDialogData = this._contractService.getNewPdfDocumentViewerDialogData();
      pdfDocumentViewerDialogData.dialogTitle = 'Disclosures & Contract for Your Loan';
      pdfDocumentViewerDialogData.dialogSubTitle = 'You must scroll through all pages of your contract before continuing.';
      pdfDocumentViewerDialogData.errorTitle = 'Disclosures & Contract';
      pdfDocumentViewerDialogData.acceptanceTitle = 'Disclosures & Contract';
      pdfDocumentViewerDialogData.requireScrollToEnd = true;

      // May need to use screen height as the width if the device browser does not flip the screen width & height on orientation changes
    //   const useScreenHeightAsWidth: boolean = window.innerWidth > window.innerHeight && screen.width < screen.height;
    //   const screenWidth: number = useScreenHeightAsWidth
    //     ? screen.height
    //     : this._deviceService.isDesktop()
    //     ? window.innerWidth
    //     : screen.width;

    //   let matDialogConfig: MatDialogConfig;
    //   if (screenWidth < this._display.dialogDisplayDialogTriggerWidth) {
    //     matDialogConfig = {
    //       width: '98%',
    //       height: '90%',
    //       disableClose: true,
    //       data: pdfDocumentViewerDialogData
    //     };
    //   } else {
    //     matDialogConfig = {
    //       width: '95%',
    //       disableClose: true,
    //       data: pdfDocumentViewerDialogData
    //     };
    //   }

      // Define and initialize matDialogConfig
      const matDialogConfig: MatDialogConfig = {
        width: '95%',
        height: '95%',
        disableClose: true,
        maxWidth: '100vw',
        maxHeight: '100vh',
        data: pdfDocumentViewerDialogData
      };

      const serviceUrl = this._getPdfFileName(this.participantId, this.noteId, this.loanCode);
      this._openPdfDocumentViewerDialog(serviceUrl, pdfDocumentViewerDialogData, matDialogConfig, returnData => {
        if (this._logging.navigationTraceLog) {
          console.log('EsignNoteComponent.viewDisclosures :: Dialog Closed');
        }

        const pdfDocumentViewerDialogResponse: PdfDocumentViewerDialogData = returnData;
        if (
          pdfDocumentViewerDialogResponse !== undefined &&
          pdfDocumentViewerDialogResponse !== null &&
          pdfDocumentViewerDialogResponse.viewOK !== undefined &&
          pdfDocumentViewerDialogResponse.viewOK !== null
        ) {
          this.step1Complete = pdfDocumentViewerDialogResponse.viewOK;

          if (this.step1Complete) {
            setTimeout(() => {
              this.esignNoteStepper.next();
            });
          }
        } else {
          this.step1Complete = false;
        }
      });
    }
  }

  viewSignedContract(): void {
    if (this._userService.isLoggedIn()) {
      const pdfDocumentViewerDialogData: PdfDocumentViewerDialogData = this._contractService.getNewPdfDocumentViewerDialogData();
      pdfDocumentViewerDialogData.dialogTitle = 'Signed Contract for Your Loan';
      pdfDocumentViewerDialogData.errorTitle = 'Signed Contract';
      pdfDocumentViewerDialogData.acceptanceTitle = 'Signed Contract';
      pdfDocumentViewerDialogData.requireScrollToEnd = false;

      // May need to use screen height as the width if the device browser does not flip the screen width & height on orientation changes
      const useScreenHeightAsWidth: boolean = window.innerWidth > window.innerHeight && screen.width < screen.height;
      const screenWidth: number = useScreenHeightAsWidth
        ? screen.height
        : this._deviceService.isDesktop()
        ? window.innerWidth
        : screen.width;

      let matDialogConfig: MatDialogConfig;
      if (screenWidth < this._display.dialogDisplayDialogTriggerWidth) {
        matDialogConfig = {
            width: '95%',
            height: '95%',
            disableClose: true,
            maxWidth: '100vw',
            maxHeight: '100vh',
          data: pdfDocumentViewerDialogData
        };
      } else {
        matDialogConfig = {
          width: '95%',
          disableClose: true,
          data: pdfDocumentViewerDialogData
        };
      }

      const serviceUrl = this._getPdfFileName(this.participantId, this.noteId, this.loanCode);
      this._openPdfDocumentViewerDialog(serviceUrl, pdfDocumentViewerDialogData, matDialogConfig, () => {
        if (this._logging.navigationTraceLog) {
          console.log('EsignNoteComponent.viewSignedContract :: Dialog Closed');
        }
      });
    }
  }

  agreeToESign(): void {
    if (this._userService.isLoggedIn()) {
      this.step1Editable = false;
      this.step2Editable = false;
      this.showEsignSpinner = true;

      const esignData: EsignData = {
        participantId: this.participantId,
        noteId: this.noteId,
        ipAddress: this.getIpAddressValue(),
        userName: this._userService.getUserName(),
        relationshipType: this.relationshipType
      };

      this._contractService.processNoteEsign(esignData).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('EsignNoteComponent.processNoteEsign :: result = ' + result);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('EsignNoteComponent.processNoteEsign :: result = ' + result);
          }

          if (result !== null && Number(result) === 0) {
            this.showEsignSuccess = true;
            this.showEsignFailure = false;
          } else {
            this.showEsignSuccess = false;
            this.showEsignFailure = true;
          }
          this.showEsignSpinner = false;
          this.esignProcessed = true;
        },
        error => this._handleError('agreeToESign.processNoteEsign', error)
      );
    }
  }

  cancelESign(): void {
    if (this._userService.isLoggedIn()) {
      this.step1Complete = false;
      this.step2Complete = false;
      this.esignProcessed = false;
      this.esignNoteStepper.reset();

      this._userService.clearNoteId();

      if (this._logging.navigationTraceLog) {
        console.log('EsignNoteComponent.cancelESign');
      }
      this._router.navigate(['esigndetails']);
    }
  }

  esignSuccess(): void {
    if (this._userService.isLoggedIn()) {
      this._userService.clearNoteId();

      if (this._logging.navigationTraceLog) {
        console.log('EsignNoteComponent.esignSuccess');
      }
      this._router.navigate(['esigndetails']);
    }
  }

  esignFailure(): void {
    if (this._userService.isLoggedIn()) {
      this._userService.clearNoteId();

      if (this._logging.navigationTraceLog) {
        console.log('EsignNoteComponent.esignFailure');
      }
      this._router.navigate(['esigndetails']);
    }
  }

  openAutoPay(): void {
    if (this._userService.isLoggedIn()) {
      this._userService.clearNoteId();

      if (this._logging.navigationTraceLog) {
        console.log('EsignNoteComponent.openAutoPay');
      }
      this._router.navigate(['autopay']);
    }
  }

  scrollToTop() {
    const contentStart = document.getElementsByClassName('mat-sidenav-content');
    if (contentStart.length > 0) {
      contentStart[0].scrollTop = 0;
    }
  }

  getIpAddressValue(): string {
    return this.publicIpAddress !== undefined &&
      this.publicIpAddress.ip !== undefined &&
      this.publicIpAddress.ip !== null &&
      this.publicIpAddress.ip.trim().length > 0
      ? this.publicIpAddress.ip.trim()
      : null;
  }

  private _getPdfFileName(participantId: number, noteId: number, loanCode: string): string {
    const esignContractRequest: EsignContractRequest = {
      loanCode: loanCode,
      participantId: participantId,
      noteId: noteId
    };

    const encodedEsignContractRequest: string = encodeURIComponent(this._utilityService.rot47(JSON.stringify(esignContractRequest)));

    let pdfUrl = this._basePDFUrl + this._apiSettings.urlSuffix + this._apiSettings.reportingServiceUrl;
    pdfUrl = pdfUrl.replace('{REQUEST}', encodedEsignContractRequest);
    return pdfUrl;
  }

  private _openPdfDocumentViewerDialog(
    serviceUrl: string,
    dialogData: PdfDocumentViewerDialogData,
    dialogConfig: MatDialogConfig,
    afterClosed: (returnData: PdfDocumentViewerDialogData) => void
  ): void {
    this.showSpinner = true;
    this._contractService.getEsignContract(serviceUrl).subscribe(
      pdf => {
        this.showSpinner = false;
        const pdfObjectUrl = URL.createObjectURL(pdf);
        dialogData.pdfUrl = pdfObjectUrl;
        dialogConfig.data = dialogData;

        const dialogRef = this.pdfDocumentViewerDialog.open(PdfDocumentViewerDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(returnData => {
          URL.revokeObjectURL(pdfObjectUrl);
          afterClosed(returnData);
        });
      },
      error => {
        this.showSpinner = false;
        this._handleError('getEsignContract', error);
      }
    );
  }
}
